import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { Catalogo } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { CatalogoService } from '../../../../../services/shared/catalogo.service';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import { PerfilesService } from 'src/app/services/perfiles/perfiles.service';
import { SignoLocalidadService } from 'src/app/services/entidades/signo-localidades.service';
import { SignoLocalidad } from 'src/app/interfaces/interface-bakend/entidades/signo-localidad.interface';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-zona-alta',
    templateUrl: './zona-alta.component.html',
    styleUrls: ['./zona-alta.component.scss'],
})
export class ZonaAltaComponent implements OnInit {
    form: FormGroup;
    catalogo: Catalogo;
    localidades: SignoLocalidad[] = []; // lista de todas las localidades
    localidadesOrdenadas: SignoLocalidad[] = []; // localidades ordenadas para mostrar
    localidadesSeleccionadas: Set<string> = new Set(); // IDs de localidades seleccionadas
    zonas: Catalogo[] = []; // todas las zonas para buscar nombres
    zonaMap: Map<string, string> = new Map(); // mapa _id -> nombre de zona

    showSpinner = false;
    spinnerText = '';

    constructor(
        private router: Router,
        private catalogoService: CatalogoService,
        private signoLocalidadService: SignoLocalidadService,
        public guiMsjService: GuiMsjService,
    ) {
        this.createForm();
        this.getCatalogo();
    }

    ngOnInit(): void {
        this.obtenerDatosIniciales(); //obtener zonas y localidades al iniciar
    }

    get zonaNoValida() {
        return this.form.get('zona').invalid && this.form.get('zona').touched;
    }

    async getCatalogo() {
        this.catalogo = await this.catalogoService.getFindByKey(
            EntidadesKeys.ZONAS,
        );
    }

    // Obtener todas las zonas para crear el mapa
    async getZonas() {
        try {
            this.zonas = await this.catalogoService.findDescendantsByKey(
                EntidadesKeys.ZONAS,
            );
            // Crear mapa rápido de búsqueda: _id -> concepto (nombre)
            this.zonaMap.clear();
            this.zonas.forEach((zona) => {
                this.zonaMap.set(zona._id, zona.concepto);
            });
        } catch (error) {
            console.error('Error al obtener zonas:', error);
        }
    }

    // Método para obtener el nombre de la zona de una localidad (maneja string o objeto)
    getNombreZona(localidad: SignoLocalidad): string {
        if (!localidad.zona) {
            return '';
        }

        // CASO 1: zona es un string (ID)
        if (typeof localidad.zona === 'string') {
            return this.zonaMap.get(localidad.zona) || '';
        }

        // CASO 2: zona es un objeto {_id: string}
        if (localidad.zona._id) {
            return this.zonaMap.get(localidad.zona._id) || '';
        }

        return '';
    }

    // Método para obtener el ID de la zona de una localidad
    getZonaId(localidad: SignoLocalidad): string {
        if (!localidad.zona) {
            return '';
        }

        if (typeof localidad.zona === 'string') {
            return localidad.zona;
        }

        return localidad.zona._id || '';
    }

    //método para obtener todas las localidades
    async getLocalidades() {
        try {
            this.localidades =
                await this.signoLocalidadService.getLocalidades();
            this.ordenarLocalidades();
        } catch (error) {
            console.error('Error al obtener localidades:', error);
        }
    }

    // Ordenar localidades: primero por zona, luego alfabéticamente
    ordenarLocalidades() {
        this.localidadesOrdenadas = [...this.localidades].sort((a, b) => {
            // Obtener nombres de zonas
            const zonaA = this.getNombreZona(a);
            const zonaB = this.getNombreZona(b);

            // Si ambas tienen zona o ambas no tienen zona, ordenar alfabéticamente por nombre
            if ((zonaA && zonaB) || (!zonaA && !zonaB)) {
                return a.nombre.localeCompare(b.nombre);
            }

            // Si solo una tiene zona, la que tiene zona va primero
            return zonaA ? -1 : 1;
        });
    }

    // Obtener todos los datos iniciales
    async obtenerDatosIniciales() {
        await Promise.all([this.getZonas(), this.getLocalidades()]);
    }

    //manejar selección de localidades
    onLocalidadSeleccionada(event: any, localidadId: string) {
        if (event.checked) {
            this.localidadesSeleccionadas.add(localidadId);
        } else {
            this.localidadesSeleccionadas.delete(localidadId);
        }
    }

    createForm() {
        this.form = new FormGroup({
            zona: new FormControl('', [Validators.required, Validators.min(3)]),
            activa: new FormControl(false, [Validators.required]),
        });
    }

    async setDataForma(): Promise<Catalogo> {
        const data: Catalogo = {
            key: null,
            concepto: this.form.value.zona,
            logico_1: this.form.value.activa,
            parent: this.catalogo._id,
            properties: {},
        };
        return data;
    }

    //método para asignar la zona a las localidades seleccionadas
    async asignarZonaALocalidades(zonaId: string) {
        if (this.localidadesSeleccionadas.size === 0) return;

        const promesas = [];

        for (const localidadId of this.localidadesSeleccionadas) {
            const localidad = this.localidades.find(
                (l) => l._id === localidadId,
            );
            if (localidad) {
                // Objeto con _id para asignar
                const datosActualizados: any = {
                    nombre: localidad.nombre,
                    activo: localidad.activo,
                    zona: { _id: zonaId }, // Objeto para asignar
                };

                // Manejar provincia - SIEMPRE como objeto
                if (localidad.provincia) {
                    // Si provincia es string, convertirlo a objeto
                    if (typeof localidad.provincia === 'string') {
                        datosActualizados.provincia = {
                            _id: localidad.provincia,
                        };
                    }
                    // Si provincia ya es objeto, mantenerlo
                    else if (typeof localidad.provincia === 'object') {
                        datosActualizados.provincia = localidad.provincia;
                    }
                    // Si no se puede determinar, enviar objeto vacío
                    else {
                        datosActualizados.provincia = {};
                    }
                }
                // Si no hay provincia, enviar objeto vacío
                else {
                    datosActualizados.provincia = {};
                }

                // Console.log para depuración
                console.log(
                    `Asignando ${localidad.nombre}:`,
                    datosActualizados,
                );

                promesas.push(
                    this.signoLocalidadService
                        .updateLocalidad(localidadId, datosActualizados)
                        .catch((error) => {
                            console.error(
                                `Error al actualizar localidad ${localidadId}:`,
                                error,
                            );
                            return null;
                        }),
                );
            }
        }

        await Promise.all(promesas);
    }

    async createZona() {
        await this.guiMsjService.msjFormSubmit('Guardando');

        try {
            // 1. Crear la zona primero
            const zonaCreada: Catalogo = await this.catalogoService.post(
                await this.setDataForma(),
            );

            console.log(
                `Zona creada: ${zonaCreada._id}, asignando ${this.localidadesSeleccionadas.size} localidades`,
            );

            // 2. Asignar la nueva zona a las localidades seleccionadas
            if (this.localidadesSeleccionadas.size > 0) {
                await this.asignarZonaALocalidades(zonaCreada._id);

                // 3. Verificar asignaciones
                const idsSeleccionadas = Array.from(
                    this.localidadesSeleccionadas,
                );
                const verificacionOk = await this.verificarLocalidadesAsignadas(
                    zonaCreada._id,
                    idsSeleccionadas,
                );

                if (!verificacionOk) {
                    console.warn(
                        'No se pudieron verificar todas las localidades asignadas',
                    );
                }
            }

            Swal.fire({
                icon: 'success',
                title: 'Zona guardada satisfactoriamente',
                showConfirmButton: false,
                timer: 1100,
            });
            this.router.navigateByUrl('/zonas-listado');
        } catch (error) {
            console.error('Error al crear zona', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al crear zona',
            });
        }
    }

    // Método de verificación (idéntico al de modificación)
    private async verificarLocalidadesAsignadas(
        zonaId: string,
        localidadesIds: string[],
    ): Promise<boolean> {
        const maxIntentos = 5;

        for (let intento = 1; intento <= maxIntentos; intento++) {
            await new Promise((resolve) => setTimeout(resolve, 5000));

            try {
                const localidadesAsignadas =
                    await this.signoLocalidadService.listarLocalidadesByZona(
                        zonaId,
                    );
                const idsAsignadas = localidadesAsignadas.map((l) => l._id);
                const todasAsignadas = localidadesIds.every((id) =>
                    idsAsignadas.includes(id),
                );

                if (todasAsignadas) {
                    return true;
                }
            } catch (error) {
                console.error(`Intento ${intento} falló:`, error);
            }
        }

        return false;
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
